import { useId, type ComponentProps, type ReactNode } from 'react';
import { Switch as RSwitch } from 'radix-ui';
import { cn } from '../../lib/cn';
import { Fieldset } from './Field';

const boxClass =
  'mt-0.5 h-7 w-7 shrink-0 cursor-pointer appearance-none rounded-md border-2 border-border-strong bg-surface ' +
  'checked:border-primary checked:bg-primary disabled:cursor-not-allowed disabled:opacity-60 ' +
  "checked:bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12.5l4.5 4.5L19 7.5'/%3E%3C/svg%3E\")] bg-center bg-no-repeat";

export interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
}

/** Native checkbox with a large target and its own label. Works with react-hook-form `register`. */
export function Checkbox({ label, hint, id, className, ...rest }: CheckboxProps) {
  const autoId = useId().replace(/:/g, '');
  const cid = id ?? `cb${autoId}`;
  const hintId = hint ? `${cid}-hint` : undefined;
  return (
    // The whole row is the label, so the touch target is at least 44px tall (R-5.7) — not just the 32px box.
    <label htmlFor={cid} className={cn('flex min-h-11 cursor-pointer items-start gap-3', className)}>
      <input
        type="checkbox"
        id={cid}
        aria-labelledby={`${cid}-label`}
        aria-describedby={cn(hintId, rest['aria-describedby']) || undefined}
        className={boxClass}
        {...rest}
      />
      <span className="flex min-h-8 flex-col justify-center">
        <span id={`${cid}-label`}>{label}</span>
        {hint && (
          <span id={hintId} className="block text-muted">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

export interface RadioOption<V extends string = string> {
  value: V;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string = string> {
  name: string;
  legend: ReactNode;
  options: RadioOption<V>[];
  value: V | null | undefined;
  onChange: (value: V) => void;
  onBlur?: () => void;
  hint?: ReactNode;
  error?: string | null;
  /** Lay options side by side (good for Yes / No). */
  inline?: boolean;
  legendSize?: 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

/**
 * Native radio buttons in a fieldset (arrow keys move between options). Controlled: use with
 * react-hook-form's `Controller`, or pass value/onChange directly.
 */
export function RadioGroup<V extends string = string>({
  name,
  legend,
  options,
  value,
  onChange,
  onBlur,
  hint,
  error,
  inline,
  legendSize,
  disabled,
  className,
}: RadioGroupProps<V>) {
  const autoId = useId().replace(/:/g, '');
  return (
    <Fieldset legend={legend} hint={hint} error={error} legendSize={legendSize} className={className}>
      <div className={cn('flex gap-3', inline ? 'flex-row flex-wrap' : 'flex-col')}>
        {options.map((o, i) => {
          const id = `rg${autoId}-${i}`;
          const hintId = o.hint ? `${id}-hint` : undefined;
          const checked = value === o.value;
          return (
            // The whole bordered option is the label: a large touch target (R-5.7).
            <label
              key={o.value}
              htmlFor={id}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border-2 px-3 py-2',
                checked ? 'border-primary bg-primary-soft' : 'border-border bg-surface',
                inline && 'min-w-32',
              )}
            >
              <input
                type="radio"
                id={id}
                name={name}
                value={o.value}
                checked={checked}
                disabled={disabled || o.disabled}
                onChange={() => onChange(o.value)}
                onBlur={onBlur}
                aria-labelledby={`${id}-label`}
                aria-describedby={hintId}
                className="mt-0.5 h-7 w-7 shrink-0 cursor-pointer accent-[var(--st-primary)]"
              />
              <span className="flex min-h-8 flex-col justify-center">
                <span id={`${id}-label`} className="font-bold">
                  {o.label}
                </span>
                {o.hint && (
                  <span id={hintId} className="block text-muted">
                    {o.hint}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </Fieldset>
  );
}

export interface SwitchProps {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/** On/off setting that takes effect immediately (use a Checkbox inside forms that are submitted). */
export function Switch({ label, hint, checked, onCheckedChange, disabled, id, className }: SwitchProps) {
  const autoId = useId().replace(/:/g, '');
  const sid = id ?? `sw${autoId}`;
  const hintId = hint ? `${sid}-hint` : undefined;
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="flex flex-col">
        <label htmlFor={sid} className="cursor-pointer font-bold">
          {label}
        </label>
        {hint && (
          <p id={hintId} className="text-muted">
            {hint}
          </p>
        )}
      </div>
      <RSwitch.Root
        id={sid}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-describedby={hintId}
        className={cn(
          'relative inline-flex h-10 w-16 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors',
          'border-border-strong bg-surface-sunken data-[state=checked]:border-primary data-[state=checked]:bg-primary',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <RSwitch.Thumb
          className={cn(
            'block h-8 w-8 translate-x-0.5 rounded-full bg-surface shadow transition-transform',
            'data-[state=checked]:translate-x-[1.6rem]',
          )}
        />
      </RSwitch.Root>
    </div>
  );
}
